import {
  buildExternalLoginUrl,
  clearAccessToken,
  getAuthHeaders,
  getAuthMode,
} from './auth'
import { hardNavigate } from './navigation'
import {refreshTokenWithLock} from './tokenRefresh'

let isRefreshing = false // Prevent multiple simultaneous refresh attempts

export class APIError extends Error {
  constructor(message: string, public readonly status: number, public readonly data: any) {
    super(message)
  }
}

type ResponseType = 'json' | 'blob' | 'text' | 'raw'

// What a rep can be shown: the server's own sentence, or the fallback when all we have is a status.
export function userMessage(e: unknown, fallback: string): string {
  if (e instanceof APIError) return e.message === `HTTP ${e.status}` ? fallback : e.message
  const message = String((e as { message?: unknown } | null)?.message || '')
  return message || fallback
}

export interface ApiFetchConfig {
  method?: 'GET' | 'POST' | 'PUT' | 'HEAD' | 'OPTIONS' | 'DELETE' | 'PATCH'
  headers?: Record<string, string>
  body?: any
  responseType?: ResponseType
  signal?: AbortSignal
  keepalive?: boolean
}

// Browsers cap in-flight keepalive bodies at 64 KiB per origin; a bigger body is sent plainly.
const KEEPALIVE_MAX_BODY_BYTES = 60_000

export async function apiFetch<T = any>(path: string | URL, config?: ApiFetchConfig & { responseType: 'json' }): Promise<T>
export async function apiFetch(path: string | URL, config: ApiFetchConfig & { responseType: 'blob' }): Promise<Blob>
export async function apiFetch(path: string | URL, config: ApiFetchConfig & { responseType: 'text' }): Promise<string>
export async function apiFetch(path: string | URL, config: ApiFetchConfig & { responseType: 'raw' }): Promise<Response>
export async function apiFetch(path: string | URL, config?: ApiFetchConfig): Promise<string | Blob | any | Response>

export async function apiFetch(path: string | URL, { method = 'GET', headers = {}, body = undefined, responseType = 'json', signal, keepalive = false }: ApiFetchConfig = {}): Promise<string | Blob | any | Response> {
  const authHeaders = getAuthHeaders()

  const url = typeof path === 'string' && !path.startsWith('http') && !path.startsWith('/api/')
    ? `/api${path.startsWith('/') ? '' : '/'}${path}`
    : path

   const isFormData = body instanceof FormData
  const h: HeadersInit = {
    ...(isFormData ? {} : {'Content-Type': 'application/json'}),
    ...authHeaders,
    ...headers,
  }

  const payload = body === undefined ? undefined : isFormData ? body : JSON.stringify(body)
  const useKeepalive =
    keepalive &&
    (payload === undefined ||
      (typeof payload === 'string' && new TextEncoder().encode(payload).length < KEEPALIVE_MAX_BODY_BYTES))
  const resp = await fetch(url, {
    method,
    headers: h,
    body: payload,
    credentials: 'include', // IMPORTANT: Include cookies
    signal,
    keepalive: useKeepalive,
  })

  if (resp.status === 401) {
    const authMode = getAuthMode()

    // In external mode, try refresh once before redirecting
    if (authMode === 'external' && !isRefreshing) {
      console.log('[API] Got 401, attempting token refresh...')
      isRefreshing = true

      try {
        const refreshSuccess = await refreshTokenWithLock()

        if (refreshSuccess) {
          console.log('[API] Refresh succeeded, retrying original request...')
          isRefreshing = false

          // Retry original request with new token
          return apiFetch(path, { method, headers, body, responseType, keepalive })
        }
      } catch (error) {
        console.error('[API] Refresh failed:', error)
      } finally {
        isRefreshing = false
      }
    }

    // Refresh failed or not in external mode, redirect to login
    clearAccessToken()

    if (authMode === 'external') {
      const currentPath = window.location.pathname + window.location.search + window.location.hash
      window.location.assign(buildExternalLoginUrl(currentPath))
      throw new Error('Authentication required')
    }
    // In legacy mode, let the router guard handle redirect
  }

  if (resp.status === 403) {
    // Never bounce /unauthorized to itself: the boot probe (/auth/me) 403s there
    // for no-access users, and a self-redirect reloads the page forever.
    if (window.location.pathname !== '/unauthorized') hardNavigate('/unauthorized')
    throw new Error('Access not enabled')
  }

  if (resp.status === 204) {
    return null
  }

  if (!resp.ok) {
    const text = await resp.text()
    let data: string | any = null
    try {
      data = text ? JSON.parse(text) : null
    } catch {}

    let detail: string
    if (typeof data?.detail === 'string') {
      detail = data.detail
    } else if (Array.isArray(data?.detail)) {
      detail = data.detail.map((e: any) => e?.msg || String(e)).join('; ')
    } else {
      detail = data?.message || `HTTP ${resp.status}`
    }
    throw new APIError(detail, resp.status, data)
  }

  switch (responseType) {
    case 'text':
      return await resp.text()
    case 'blob':
      return await resp.blob()
    case 'json':
      return await resp.json()
    case 'raw':
      return resp
  }

}
